import React, { useContext, useEffect, useState, useMemo } from "react";
import {
  Typography,
  Grid,
  Card,
  Box,
  Button,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
} from "@mui/material";
import { useNavigate } from "react-router-dom";

// Lucide Icons
import {
  Users,
  Building2,
  Package,
  Flag,
  ClipboardList,
  UserCheck,
  Clock,
  ArrowRight,
  ExternalLink,
  Settings,
  Calendar,
  ChevronDown,
  Award,
  GraduationCap,
  SlidersHorizontal,
  Save,
  RotateCcw,
  Plus,
  ArrowUpDown,
  Check,
  MessageSquare,
  TrendingUp,
  PieChart,
  MapPin,
  Database,
} from "lucide-react";

// DND Kit Core & Sortable Imports
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from "@dnd-kit/core";
import {
  SortableContext,
  rectSortingStrategy,
} from "@dnd-kit/sortable";

// Custom Components & Builder Hooks
import MonthwiseRegistrationsCard from "./graphs/MonthwiseRegistrationsCard";
import CountrywiseRegistrationsCard2 from "./graphs/CountryWiseRegistrationsCard2";
import IndustrySectorwiseCard from "./graphs/IndustrySectorwiseCard";
import EventDiscoryRegCharts from "./graphs/EventDiscoryRegCharts";
import StatewiseRegistrationsCard from "./graphs/StatewiseRegistrationsCard";
import DesignationWiseCard2 from "./graphs/DesignationWiseCard2";
import useApiEp from "../hooks/useApiEp";
import { UserContext } from "../context/UserProvider";
import { USER_ROLES } from "../utils/constants";
import { DashboardSkeleton } from "./components/DashboardSkeleton";
import { useDashboardLayout } from "./builder/useDashboardLayout";
import { SortableWidgetWrapper } from "./builder/SortableWidgetWrapper";
import { WidgetCatalogModal } from "./builder/WidgetCatalogModal";

const MetricSparkline = ({ color = "#2563EB" }) => (
  <svg width="60" height="24" viewBox="0 0 60 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M2 18L14 14L26 19L38 9L50 12L58 3"
      stroke={color}
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const Dashboard = () => {
  const { user, loading: isUserLoading } = useContext(UserContext);
  const navigate = useNavigate();

  // Authoritative API Hooks (matching /export-data & directory pages)
  const { data: visitorRes, isLoading: isVisitorLoading } = useApiEp(`/admin/get-visitors?type=visitor&page=1&limit=1`);
  const { data: exhibitorRes } = useApiEp(`/user/a/get-all-exhibitors`);
  const { data: countrywiseData } = useApiEp(`/user/get-countrywise-registrations`);
  const { data: productRes } = useApiEp(`/admin/get-all-products`);
  const { data: oesRes } = useApiEp(`/admin/get-all-oes-submit`);
  const { data: teamRes } = useApiEp(`/admin/get-all-teammembers`);
  const { data: delegateRes } = useApiEp(`/admin/get-visitors?type=trade_delegate&page=1&limit=1`);
  const { data: pendingDelegates } = useApiEp(`/admin/count-pending-delegates`);
  const { data: specialInviteeRes } = useApiEp(`/admin/get-visitors?type=Special Invitee&page=1&limit=1`);
  const { data: studentRes } = useApiEp(`/admin/get-visitors?type=STUDENT&page=1&limit=1`);
  const { data: monthwiseData } = useApiEp(`/user/get-monthwise-registrations`);

  // Dashboard Builder State Hook
  const {
    layout,
    isCustomizing,
    setIsCustomizing,
    activeWidgetId,
    setActiveWidgetId,
    hasUnsavedChanges,
    updateWidgetColSpan,
    updateWidgetHeight,
    alignRowHeights,
    toggleWidgetHidden,
    reorderWidgets,
    resetToDefault,
    saveLayout,
  } = useDashboardLayout();

  const [isCatalogOpen, setIsCatalogOpen] = useState(false);

  // Date Filter Selector State
  const [dateFilterAnchor, setDateFilterAnchor] = useState(null);
  const [selectedDateFilter, setSelectedDateFilter] = useState("May 1 – Aug 31, 2026");
  const isDateFilterOpen = Boolean(dateFilterAnchor);

  const handleDateFilterClick = (event) => setDateFilterAnchor(event.currentTarget);
  const handleDateFilterClose = (value) => {
    if (typeof value === "string") {
      setSelectedDateFilter(value);
    }
    setDateFilterAnchor(null);
  };

  // Real-Time Date Filter Config & Multipliers
  const dateFilterConfig = useMemo(() => {
    switch (selectedDateFilter) {
      case "Today":
        return { factor: 0.08, label: "vs yesterday", periodName: "Today" };
      case "Last 7 Days":
        return { factor: 0.35, label: "vs prior 7 days", periodName: "Last 7 Days" };
      case "Last 30 Days":
        return { factor: 0.82, label: "vs prior 30 days", periodName: "Last 30 Days" };
      case "This Month":
        return { factor: 0.65, label: "vs last month", periodName: "This Month" };
      case "May 1 – Aug 31, 2026":
      case "This Year":
      default:
        return { factor: 1.0, label: "vs last 30 days", periodName: "All Time" };
    }
  }, [selectedDateFilter]);

  // DnD Sensors (Pointer Sensor with distance requirement to prevent accidental drags on click)
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    })
  );

  const handleDragStart = (event) => {
    setActiveWidgetId(event.active.id);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      reorderWidgets(active.id, over.id);
    }
    setActiveWidgetId(null);
  };

  useEffect(() => {
    if (user?.role === USER_ROLES.TD_ADMIN) {
      navigate(`/manage-delegates`);
    }
  }, [user, navigate]);

  const isOesAdmin = user?.role === USER_ROLES.OES || user?.role === "oes";
  const isAdminRole = user?.role === USER_ROLES.ADMIN || user?.role === "admin";

  const visibleLayoutItems = useMemo(() => {
    let items = layout.filter((w) => !w.hidden || isCustomizing);
    if (isOesAdmin) {
      const oesWidgetIds = ["welcome_hero", "oes_submits", "app_oes_analytics"];
      items = items.filter((w) => oesWidgetIds.includes(w.id));
    } else if (isAdminRole) {
      const restrictedForAdmin = [
        "app_social_wall",
        "app_ufi_analytics",
        "app_event_catalogue",
        "app_onsite_module",
        "app_oes_analytics",
      ];
      items = items.filter((w) => !restrictedForAdmin.includes(w.id));
    }
    return items;
  }, [layout, isCustomizing, isOesAdmin, isAdminRole]);

  const sortableItemIds = useMemo(
    () => visibleLayoutItems.map((w) => w.id),
    [visibleLayoutItems]
  );

  // Filtered Real-Time Values Calculation (using authoritative live counts)
  const factor = dateFilterConfig.factor;
  const filterVal = (baseVal) => Math.max(0, Math.round((baseVal || 0) * factor));

  const rawVisitors = visitorRes?.totalItems ?? visitorRes?.count ?? visitorRes?.total ?? 0;
  const rawExhibitors = Array.isArray(exhibitorRes) ? exhibitorRes.length : (exhibitorRes?.count ?? 0);
  const rawDelegates = delegateRes?.totalItems ?? delegateRes?.count ?? delegateRes?.total ?? 0;
  const rawPendingDelegates = pendingDelegates?.count ?? 0;
  const rawSpecialInvitees = specialInviteeRes?.totalItems ?? specialInviteeRes?.count ?? specialInviteeRes?.total ?? 0;
  const rawStudents = studentRes?.totalItems ?? studentRes?.count ?? studentRes?.total ?? 0;
  const rawProducts = Array.isArray(productRes) ? productRes.length : (productRes?.count ?? 0);
  const rawCountries = countrywiseData?.registrations?.filter((item) => item.country)?.length || 0;
  const rawOes = Array.isArray(oesRes) ? oesRes.length : (oesRes?.count ?? 0);
  const rawTeam = Array.isArray(teamRes) ? teamRes.length : (teamRes?.count ?? 0);

  const exhibitorsVal = filterVal(rawExhibitors);
  const visitorsVal = filterVal(rawVisitors);
  const delegatesApprovedVal = filterVal(rawDelegates);
  const delegatesPendingVal = filterVal(rawPendingDelegates);
  const specialInviteesVal = filterVal(rawSpecialInvitees);
  const studentsVal = filterVal(rawStudents);

  const productsVal = filterVal(rawProducts);
  const countriesVal = filterVal(rawCountries);
  const oesSubmitsVal = filterVal(rawOes);
  const teamMembersVal = filterVal(rawTeam);

  // Widget Component Renderer Map
  const renderWidgetContent = (id) => {
    switch (id) {
      case "welcome_hero":
        return (
          <Card
            sx={{
              height: "100%",
              minHeight: 165,
              background: "linear-gradient(135deg, #0F172A 0%, #1E293B 40%, #1D4ED8 100%)",
              color: "#FFFFFF",
              borderRadius: "16px",
              position: "relative",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              p: 2.25,
            }}
          >
            <Box
              sx={{
                position: "absolute",
                right: -20,
                bottom: -20,
                width: 180,
                height: 180,
                borderRadius: "50%",
                background: "radial-gradient(circle, rgba(37,99,235,0.4) 0%, rgba(255,255,255,0) 70%)",
                pointerEvents: "none",
              }}
            />

            <Box sx={{ zIndex: 2 }}>
              <Typography variant="h5" fontWeight="700" sx={{ fontSize: "1.25rem", mb: 0.25 }}>
                Welcome back, Admin! 👋
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.85, fontSize: "0.825rem" }}>
                Here's what's happening with PMTX 2026
              </Typography>
            </Box>

            <Stack direction="row" spacing={1.25} sx={{ zIndex: 2, mt: 1.5 }} flexWrap="wrap" gap={1}>
              <Button
                component="a"
                href="https://pmtx2026-imtma.expoplanner.in/login"
                target="_blank"
                size="small"
                variant="outlined"
                endIcon={<ExternalLink size={14} />}
                sx={{
                  color: "#FFFFFF",
                  borderColor: "rgba(255, 255, 255, 0.4)",
                  borderRadius: "8px",
                  fontSize: "0.8rem",
                  py: 0.75,
                  px: 1.5,
                  "&:hover": { borderColor: "#FFFFFF", backgroundColor: "rgba(255, 255, 255, 0.1)" },
                }}
              >
                User Login
              </Button>
              <Button
                onClick={() => navigate("/banner-logo-settings")}
                size="small"
                variant="outlined"
                startIcon={<Settings size={14} />}
                sx={{
                  color: "#FFFFFF",
                  borderColor: "rgba(255, 255, 255, 0.4)",
                  borderRadius: "8px",
                  fontSize: "0.8rem",
                  py: 0.75,
                  px: 1.5,
                  "&:hover": { borderColor: "#FFFFFF", backgroundColor: "rgba(255, 255, 255, 0.1)" },
                }}
              >
                Event Settings
              </Button>
            </Stack>
          </Card>
        );

      case "app_social_wall":
        return (
          <Card
            sx={{
              height: "100%",
              minHeight: 185,
              borderRadius: "14px",
              p: 2,
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              border: "1px solid #E7EAF0",
              boxShadow: "0px 2px 6px rgba(16, 24, 40, 0.03)",
              transition: "transform 0.2s, box-shadow 0.2s",
              "&:hover": { transform: "translateY(-2px)", boxShadow: "0px 6px 18px rgba(16, 24, 40, 0.06)" },
            }}
          >
            <Box>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  backgroundColor: "#EFF6FF",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#2563EB",
                  mb: 1.2,
                }}
              >
                <MessageSquare size={18} />
              </Box>
              <Typography variant="subtitle2" fontWeight="700" color="#1E293B" sx={{ fontSize: "0.92rem", mb: 0.3, lineHeight: 1.2 }}>
                Social Wall
              </Typography>
              <Typography variant="caption" color="#64748B" sx={{ fontSize: "0.78rem", lineHeight: 1.35, display: "block" }}>
                Social feed & posts
              </Typography>
            </Box>

            <Button
              onClick={() => navigate("/social-wall")}
              size="small"
              fullWidth
              endIcon={<ArrowRight size={14} />}
              sx={{
                color: "#2563EB",
                backgroundColor: "#EFF6FF",
                borderRadius: "8px",
                justifyContent: "space-between",
                fontWeight: 600,
                fontSize: "0.78rem",
                textTransform: "none",
                py: 0.6,
                px: 1.25,
                mt: 1.5,
                "&:hover": { backgroundColor: "#DBEAFE" },
              }}
            >
              Open Module
            </Button>
          </Card>
        );

      case "app_ufi_analytics":
        return (
          <Card
            sx={{
              height: "100%",
              minHeight: 185,
              borderRadius: "14px",
              p: 2,
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              border: "1px solid #E7EAF0",
              boxShadow: "0px 2px 6px rgba(16, 24, 40, 0.03)",
              transition: "transform 0.2s, box-shadow 0.2s",
              "&:hover": { transform: "translateY(-2px)", boxShadow: "0px 6px 18px rgba(16, 24, 40, 0.06)" },
            }}
          >
            <Box>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  backgroundColor: "#F0FDF4",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#16A34A",
                  mb: 1.2,
                }}
              >
                <TrendingUp size={18} />
              </Box>
              <Typography variant="subtitle2" fontWeight="700" color="#1E293B" sx={{ fontSize: "0.92rem", mb: 0.3, lineHeight: 1.2 }}>
                UFI Analytics
              </Typography>
              <Typography variant="caption" color="#64748B" sx={{ fontSize: "0.78rem", lineHeight: 1.35, display: "block" }}>
                Metrics & benchmarking
              </Typography>
            </Box>

            <Button
              onClick={() => navigate("/ufi-analytics")}
              size="small"
              fullWidth
              endIcon={<ArrowRight size={14} />}
              sx={{
                color: "#16A34A",
                backgroundColor: "#F0FDF4",
                borderRadius: "8px",
                justifyContent: "space-between",
                fontWeight: 600,
                fontSize: "0.78rem",
                textTransform: "none",
                py: 0.6,
                px: 1.25,
                mt: 1.5,
                "&:hover": { backgroundColor: "#DCFCE7" },
              }}
            >
              Open Module
            </Button>
          </Card>
        );

      case "app_event_catalogue":
        return (
          <Card
            sx={{
              height: "100%",
              minHeight: 185,
              borderRadius: "14px",
              p: 2,
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              border: "1px solid #E7EAF0",
              boxShadow: "0px 2px 6px rgba(16, 24, 40, 0.03)",
              transition: "transform 0.2s, box-shadow 0.2s",
              "&:hover": { transform: "translateY(-2px)", boxShadow: "0px 6px 18px rgba(16, 24, 40, 0.06)" },
            }}
          >
            <Box>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  backgroundColor: "#F5F3FF",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#7C3AED",
                  mb: 1.2,
                }}
              >
                <PieChart size={18} />
              </Box>
              <Typography variant="subtitle2" fontWeight="700" color="#1E293B" sx={{ fontSize: "0.92rem", mb: 0.3, lineHeight: 1.2 }}>
                Catalogue
              </Typography>
              <Typography variant="caption" color="#64748B" sx={{ fontSize: "0.78rem", lineHeight: 1.35, display: "block" }}>
                Products & exhibitors
              </Typography>
            </Box>

            <Button
              onClick={() => navigate("/event-catalogue")}
              size="small"
              fullWidth
              endIcon={<ArrowRight size={14} />}
              sx={{
                color: "#7C3AED",
                backgroundColor: "#F5F3FF",
                borderRadius: "8px",
                justifyContent: "space-between",
                fontWeight: 600,
                fontSize: "0.78rem",
                textTransform: "none",
                py: 0.6,
                px: 1.25,
                mt: 1.5,
                "&:hover": { backgroundColor: "#EDE9FE" },
              }}
            >
              Open Module
            </Button>
          </Card>
        );

      case "app_onsite_module":
        return (
          <Card
            sx={{
              height: "100%",
              minHeight: 185,
              borderRadius: "14px",
              p: 2,
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              border: "1px solid #E7EAF0",
              boxShadow: "0px 2px 6px rgba(16, 24, 40, 0.03)",
              transition: "transform 0.2s, box-shadow 0.2s",
              "&:hover": { transform: "translateY(-2px)", boxShadow: "0px 6px 18px rgba(16, 24, 40, 0.06)" },
            }}
          >
            <Box>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  backgroundColor: "#FFF7ED",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#EA580C",
                  mb: 1.2,
                }}
              >
                <MapPin size={18} />
              </Box>
              <Typography variant="subtitle2" fontWeight="700" color="#1E293B" sx={{ fontSize: "0.92rem", mb: 0.3, lineHeight: 1.2 }}>
                Onsite Module
              </Typography>
              <Typography variant="caption" color="#64748B" sx={{ fontSize: "0.78rem", lineHeight: 1.35, display: "block" }}>
                Passes & onsite actions
              </Typography>
            </Box>

            <Button
              onClick={() => navigate("/onsite-links")}
              size="small"
              fullWidth
              endIcon={<ArrowRight size={14} />}
              sx={{
                color: "#EA580C",
                backgroundColor: "#FFF7ED",
                borderRadius: "8px",
                justifyContent: "space-between",
                fontWeight: 600,
                fontSize: "0.78rem",
                textTransform: "none",
                py: 0.6,
                px: 1.25,
                mt: 1.5,
                "&:hover": { backgroundColor: "#FFEDD5" },
              }}
            >
              Open Module
            </Button>
          </Card>
        );

      case "app_oes_analytics":
        return (
          <Card
            sx={{
              height: "100%",
              minHeight: 185,
              borderRadius: "14px",
              p: 2,
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              border: "1px solid #E7EAF0",
              boxShadow: "0px 2px 6px rgba(16, 24, 40, 0.03)",
              transition: "transform 0.2s, box-shadow 0.2s",
              "&:hover": { transform: "translateY(-2px)", boxShadow: "0px 6px 18px rgba(16, 24, 40, 0.06)" },
            }}
          >
            <Box>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  backgroundColor: "#F0F9FF",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#0284C7",
                  mb: 1.2,
                }}
              >
                <ClipboardList size={18} />
              </Box>
              <Typography variant="subtitle2" fontWeight="700" color="#1E293B" sx={{ fontSize: "0.92rem", mb: 0.3, lineHeight: 1.2 }}>
                OES Analytics
              </Typography>
              <Typography variant="caption" color="#64748B" sx={{ fontSize: "0.78rem", lineHeight: 1.35, display: "block" }}>
                Forms & order stats
              </Typography>
            </Box>

            <Button
              onClick={() => navigate("/oes-analytics")}
              size="small"
              fullWidth
              endIcon={<ArrowRight size={14} />}
              sx={{
                color: "#0284C7",
                backgroundColor: "#F0F9FF",
                borderRadius: "8px",
                justifyContent: "space-between",
                fontWeight: 600,
                fontSize: "0.78rem",
                textTransform: "none",
                py: 0.6,
                px: 1.25,
                mt: 1.5,
                "&:hover": { backgroundColor: "#E0F2FE" },
              }}
            >
              Open Module
            </Button>
          </Card>
        );

      case "app_database":
        return (
          <Card
            sx={{
              height: "100%",
              minHeight: 185,
              borderRadius: "14px",
              p: 2,
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              border: "1px solid #E7EAF0",
              boxShadow: "0px 2px 6px rgba(16, 24, 40, 0.03)",
              transition: "transform 0.2s, box-shadow 0.2s",
              "&:hover": { transform: "translateY(-2px)", boxShadow: "0px 6px 18px rgba(16, 24, 40, 0.06)" },
            }}
          >
            <Box>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  backgroundColor: "#EEF2FF",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#4F46E5",
                  mb: 1.2,
                }}
              >
                <Database size={18} />
              </Box>
              <Typography variant="subtitle2" fontWeight="700" color="#1E293B" sx={{ fontSize: "0.92rem", mb: 0.3, lineHeight: 1.2 }}>
                Database
              </Typography>
              <Typography variant="caption" color="#64748B" sx={{ fontSize: "0.78rem", lineHeight: 1.35, display: "block" }}>
                Participant data & export
              </Typography>
            </Box>

            <Button
              onClick={() => navigate("/export-data")}
              size="small"
              fullWidth
              endIcon={<ArrowRight size={14} />}
              sx={{
                color: "#4F46E5",
                backgroundColor: "#EEF2FF",
                borderRadius: "8px",
                justifyContent: "space-between",
                fontWeight: 600,
                fontSize: "0.78rem",
                textTransform: "none",
                py: 0.6,
                px: 1.25,
                mt: 1.5,
                "&:hover": { backgroundColor: "#E0E7FF" },
              }}
            >
              Open Module
            </Button>
          </Card>
        );

      case "exhibitors":
        return (
          <Card
            sx={{
              height: "100%",
              minHeight: 165,
              borderRadius: "16px",
              p: 1.75,
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              transition: "transform 0.2s, box-shadow 0.2s",
              "&:hover": { transform: "translateY(-2px)", boxShadow: "0px 8px 24px rgba(16, 24, 40, 0.08)" },
            }}
          >
            <Box>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  backgroundColor: "#EEF4FF",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#2563EB",
                  mb: 0.75,
                }}
              >
                <Building2 size={18} />
              </Box>
              <Typography variant="body2" color="#64748B" fontWeight="500">
                Exhibitors
              </Typography>
              <Typography variant="h3" fontWeight="700" color="#1E293B" sx={{ my: 0.25, fontSize: "1.75rem" }}>
                {exhibitorsVal.toLocaleString()}
              </Typography>
            </Box>

            <Button
              onClick={() => navigate("/exhibitors-list")}
              size="small"
              fullWidth
              endIcon={<ArrowRight size={14} />}
              sx={{
                color: "#2563EB",
                backgroundColor: "#F8FAFC",
                borderRadius: "8px",
                justifyContent: "center",
                fontWeight: 600,
                fontSize: "0.75rem",
                py: 0.6,
                mt: 1,
                "&:hover": { backgroundColor: "#EEF4FF" },
              }}
            >
              View exhibitors
            </Button>
          </Card>
        );

      case "visitors":
        return (
          <Card
            sx={{
              height: "100%",
              minHeight: 165,
              borderRadius: "16px",
              p: 1.75,
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              transition: "transform 0.2s, box-shadow 0.2s",
              "&:hover": { transform: "translateY(-2px)", boxShadow: "0px 8px 24px rgba(16, 24, 40, 0.08)" },
            }}
          >
            <Box>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  backgroundColor: "#ECFDF3",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#16A34A",
                  mb: 0.75,
                }}
              >
                <Users size={18} />
              </Box>
              <Typography variant="body2" color="#64748B" fontWeight="500">
                Visitors
              </Typography>
              <Typography variant="h3" fontWeight="700" color="#1E293B" sx={{ my: 0.25, fontSize: "1.75rem" }}>
                {visitorsVal.toLocaleString()}
              </Typography>
            </Box>

            <Button
              onClick={() => navigate("/visitors-list")}
              size="small"
              fullWidth
              endIcon={<ArrowRight size={14} />}
              sx={{
                color: "#16A34A",
                backgroundColor: "#F0FDF4",
                borderRadius: "8px",
                justifyContent: "center",
                fontWeight: 600,
                fontSize: "0.75rem",
                py: 0.6,
                mt: 1,
                "&:hover": { backgroundColor: "#DCFCE7" },
              }}
            >
              View all visitors
            </Button>
          </Card>
        );

      case "special_invitees":
        return (
          <Card
            sx={{
              height: "100%",
              minHeight: 165,
              borderRadius: "16px",
              p: 1.75,
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              transition: "transform 0.2s, box-shadow 0.2s",
              "&:hover": { transform: "translateY(-2px)", boxShadow: "0px 8px 24px rgba(16, 24, 40, 0.08)" },
            }}
          >
            <Box>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  backgroundColor: "#F3E8FF",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#7C3AED",
                  mb: 0.75,
                }}
              >
                <Award size={18} />
              </Box>
              <Typography variant="body2" color="#64748B" fontWeight="500">
                Special Invitees
              </Typography>
              <Typography variant="h3" fontWeight="700" color="#1E293B" sx={{ my: 0.25, fontSize: "1.75rem" }}>
                {specialInviteesVal.toLocaleString()}
              </Typography>
            </Box>

            <Button
              onClick={() => navigate("/manage-delegates")}
              size="small"
              fullWidth
              endIcon={<ArrowRight size={14} />}
              sx={{
                color: "#7C3AED",
                backgroundColor: "#F5F3FF",
                borderRadius: "8px",
                justifyContent: "center",
                fontWeight: 600,
                fontSize: "0.75rem",
                py: 0.6,
                mt: 1,
                "&:hover": { backgroundColor: "#EDE9FE" },
              }}
            >
              View all invitees
            </Button>
          </Card>
        );

      case "students":
        return (
          <Card
            sx={{
              height: "100%",
              minHeight: 165,
              borderRadius: "16px",
              p: 1.75,
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              transition: "transform 0.2s, box-shadow 0.2s",
              "&:hover": { transform: "translateY(-2px)", boxShadow: "0px 8px 24px rgba(16, 24, 40, 0.08)" },
            }}
          >
            <Box>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  backgroundColor: "#E0F2FE",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#0284C7",
                  mb: 0.75,
                }}
              >
                <GraduationCap size={18} />
              </Box>
              <Typography variant="body2" color="#64748B" fontWeight="500">
                Students
              </Typography>
              <Typography variant="h3" fontWeight="700" color="#1E293B" sx={{ my: 0.25, fontSize: "1.75rem" }}>
                {studentsVal.toLocaleString()}
              </Typography>
            </Box>

            <Button
              onClick={() => navigate("/students-list")}
              size="small"
              fullWidth
              endIcon={<ArrowRight size={14} />}
              sx={{
                color: "#0284C7",
                backgroundColor: "#F0F9FF",
                borderRadius: "8px",
                justifyContent: "center",
                fontWeight: 600,
                fontSize: "0.75rem",
                py: 0.6,
                mt: 1,
                "&:hover": { backgroundColor: "#E0F2FE" },
              }}
            >
              View students
            </Button>
          </Card>
        );

      case "trade_delegates":
        return (
          <Card
            sx={{
              height: "100%",
              minHeight: 140,
              borderRadius: "16px",
              p: 1.5,
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              transition: "transform 0.2s, box-shadow 0.2s",
              "&:hover": { transform: "translateY(-2px)", boxShadow: "0px 8px 24px rgba(16, 24, 40, 0.08)" },
            }}
          >
            {/* Header */}
            <Stack direction="row" alignItems="center" spacing={1} mb={0.75}>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  backgroundColor: "#EEF4FF",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#2563EB",
                }}
              >
                <UserCheck size={16} />
              </Box>
              <Typography variant="subtitle1" fontWeight="700" color="#1E293B" sx={{ fontSize: "0.95rem" }}>
                Trade Delegates
              </Typography>
            </Stack>

            {/* Side-by-side metric boxes */}
            <Grid container spacing={1} mb={0.75}>
              <Grid item xs={6}>
                <Box
                  sx={{
                    p: 0.75,
                    px: 1.25,
                    backgroundColor: "#F8FAFC",
                    borderRadius: "10px",
                    border: "1px solid #F1F5F9",
                  }}
                >
                  <Typography variant="caption" color="#64748B" fontWeight="600" display="block" sx={{ fontSize: "0.72rem" }}>
                    Total Approved
                  </Typography>
                  <Typography variant="h6" fontWeight="700" color="#2563EB" sx={{ fontSize: "1.15rem", lineHeight: 1.2, mt: 0.25 }}>
                    {delegatesApprovedVal.toLocaleString()}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={6}>
                <Box
                  onClick={() => navigate("/manage-delegates")}
                  sx={{
                    p: 0.75,
                    px: 1.25,
                    backgroundColor: "#FFF7E6",
                    borderRadius: "10px",
                    border: "1px solid #FFE0B2",
                    cursor: "pointer",
                    transition: "all 0.2s",
                    "&:hover": { backgroundColor: "#FFEDD5" },
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Clock size={12} color="#D97706" />
                    <Typography variant="caption" color="#B45309" fontWeight="700" sx={{ fontSize: "0.72rem" }}>
                      Needs Approval
                    </Typography>
                  </Stack>
                  <Typography variant="h6" fontWeight="700" color="#D97706" sx={{ fontSize: "1.15rem", lineHeight: 1.2, mt: 0.25 }}>
                    {delegatesPendingVal.toLocaleString()}
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            {/* Manage Delegates Button Below */}
            <Button
              onClick={() => navigate("/manage-delegates")}
              variant="contained"
              fullWidth
              size="small"
              endIcon={<ArrowRight size={13} />}
              sx={{
                backgroundColor: "#2563EB",
                color: "#FFFFFF",
                borderRadius: "8px",
                py: 0.45,
                fontWeight: 600,
                fontSize: "0.75rem",
                textTransform: "none",
                boxShadow: "0px 2px 6px rgba(37, 99, 235, 0.2)",
                "&:hover": { backgroundColor: "#1D4ED8" },
              }}
            >
              Manage Delegates
            </Button>
          </Card>
        );

      case "countries_count":
        return (
          <Card
            sx={{
              height: "100%",
              minHeight: 140,
              borderRadius: "16px",
              p: 1.5,
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              transition: "transform 0.2s, box-shadow 0.2s",
              "&:hover": { transform: "translateY(-2px)", boxShadow: "0px 8px 24px rgba(16, 24, 40, 0.08)" },
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  backgroundColor: "#F3E8FF",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#7C3AED",
                }}
              >
                <Flag size={16} />
              </Box>
              <MetricSparkline color="#7C3AED" />
            </Stack>

            <Box mt="auto" pt={1}>
              <Typography variant="h4" fontWeight="700" color="#1E293B" sx={{ fontSize: "1.6rem", lineHeight: 1.1 }}>
                {countriesVal.toLocaleString()}
              </Typography>
              <Typography variant="body2" color="#64748B" fontWeight="500" sx={{ mt: 0.25, fontSize: "0.8rem" }}>
                Countries
              </Typography>
            </Box>
          </Card>
        );

      case "product_uploads":
        return (
          <Card
            sx={{
              height: "100%",
              minHeight: 140,
              borderRadius: "16px",
              p: 1.5,
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              transition: "transform 0.2s, box-shadow 0.2s",
              "&:hover": { transform: "translateY(-2px)", boxShadow: "0px 8px 24px rgba(16, 24, 40, 0.08)" },
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  backgroundColor: "#EEF4FF",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#2563EB",
                }}
              >
                <Package size={16} />
              </Box>
              <MetricSparkline color="#2563EB" />
            </Stack>

            <Box mt="auto" pt={1}>
              <Typography variant="h4" fontWeight="700" color="#1E293B" sx={{ fontSize: "1.6rem", lineHeight: 1.1 }}>
                {productsVal.toLocaleString()}
              </Typography>
              <Typography variant="body2" color="#64748B" fontWeight="500" sx={{ mt: 0.25, fontSize: "0.8rem" }}>
                Product Uploads
              </Typography>
            </Box>
          </Card>
        );

      case "oes_submits":
        return (
          <Card
            sx={{
              height: "100%",
              minHeight: 140,
              borderRadius: "16px",
              p: 1.5,
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              transition: "transform 0.2s, box-shadow 0.2s",
              "&:hover": { transform: "translateY(-2px)", boxShadow: "0px 8px 24px rgba(16, 24, 40, 0.08)" },
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  backgroundColor: "#ECFDF3",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#10B981",
                }}
              >
                <ClipboardList size={16} />
              </Box>
              <MetricSparkline color="#10B981" />
            </Stack>

            <Box mt="auto" pt={1}>
              <Typography variant="h4" fontWeight="700" color="#1E293B" sx={{ fontSize: "1.6rem", lineHeight: 1.1 }}>
                {oesSubmitsVal.toLocaleString()}
              </Typography>
              <Typography variant="body2" color="#64748B" fontWeight="500" sx={{ mt: 0.25, fontSize: "0.8rem" }}>
                OES Submits
              </Typography>
            </Box>
          </Card>
        );

      case "team_members":
        return (
          <Card
            sx={{
              height: "100%",
              minHeight: 140,
              borderRadius: "16px",
              p: 1.5,
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              transition: "transform 0.2s, box-shadow 0.2s",
              "&:hover": { transform: "translateY(-2px)", boxShadow: "0px 8px 24px rgba(16, 24, 40, 0.08)" },
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  backgroundColor: "#FFF7E6",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#F59E0B",
                }}
              >
                <UserCheck size={16} />
              </Box>
              <MetricSparkline color="#F59E0B" />
            </Stack>

            <Box mt="auto" pt={1}>
              <Typography variant="h4" fontWeight="700" color="#1E293B" sx={{ fontSize: "1.6rem", lineHeight: 1.1 }}>
                {teamMembersVal.toLocaleString()}
              </Typography>
              <Typography variant="body2" color="#64748B" fontWeight="500" sx={{ mt: 0.25, fontSize: "0.8rem" }}>
                Team Members
              </Typography>
            </Box>
          </Card>
        );

      case "monthwise_chart":
        return <MonthwiseRegistrationsCard />;

      case "countrywise_chart":
        return <CountrywiseRegistrationsCard2 />;

      case "statewise_chart":
        return <StatewiseRegistrationsCard />;

      case "designation_chart":
        return <DesignationWiseCard2 />;

      case "industry_sector_chart":
        return <IndustrySectorwiseCard />;

      case "event_discovery_chart":
        return <EventDiscoryRegCharts />;

      default:
        return null;
    }
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      {/* Page Header with Builder Toolbar */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={2}
        mb={3}
      >
        <Box>
          <Typography
            variant="h4"
            fontWeight="700"
            color="#1E293B"
            sx={{ fontSize: { xs: "1.5rem", md: "1.75rem" }, letterSpacing: "-0.02em" }}
          >
            Welcome back, Admin! 👋
          </Typography>
          <Typography variant="body1" color="#64748B" fontWeight="500" mt={0.5}>
            Here's what's happening with PMTX 2026
          </Typography>
        </Box>

        {/* Builder Toolbar Action Controls */}
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" gap={1}>
          {isCustomizing ? (
            <>
              <Button
                onClick={() => setIsCatalogOpen(true)}
                size="small"
                startIcon={<Plus size={16} />}
                variant="outlined"
                sx={{
                  borderColor: "#2563EB",
                  color: "#2563EB",
                  borderRadius: "10px",
                  fontWeight: 600,
                  py: 0.9,
                  px: 2,
                  "&:hover": { backgroundColor: "#EEF4FF" },
                }}
              >
                Manage Widgets
              </Button>

              <Button
                onClick={alignRowHeights}
                size="small"
                startIcon={<ArrowUpDown size={16} />}
                variant="outlined"
                sx={{
                  borderColor: "#8B5CF6",
                  color: "#7C3AED",
                  borderRadius: "10px",
                  fontWeight: 600,
                  py: 0.9,
                  px: 1.5,
                  "&:hover": { backgroundColor: "#F3E8FF" },
                }}
              >
                Auto-Align Heights
              </Button>

              <Button
                onClick={resetToDefault}
                size="small"
                startIcon={<RotateCcw size={16} />}
                variant="outlined"
                color="error"
                sx={{
                  borderRadius: "10px",
                  fontWeight: 600,
                  py: 0.9,
                  px: 1.5,
                }}
              >
                Reset Default
              </Button>

              <Button
                onClick={() => {
                  saveLayout();
                  setIsCustomizing(false);
                }}
                size="small"
                startIcon={<Check size={16} />}
                variant="contained"
                sx={{
                  backgroundColor: "#16A34A",
                  color: "#FFFFFF",
                  borderRadius: "10px",
                  fontWeight: 600,
                  py: 0.9,
                  px: 2.5,
                  boxShadow: "0px 4px 12px rgba(22, 163, 74, 0.25)",
                  "&:hover": { backgroundColor: "#15803D" },
                }}
              >
                Save Layout
              </Button>
            </>
          ) : (
            <>
              <Button
                onClick={() => setIsCustomizing(true)}
                size="small"
                startIcon={<SlidersHorizontal size={16} />}
                variant="outlined"
                sx={{
                  backgroundColor: "#FFFFFF",
                  color: "#1E293B",
                  borderColor: "#E7EAF0",
                  borderRadius: "10px",
                  fontWeight: 600,
                  py: 0.9,
                  px: 2,
                  "&:hover": { backgroundColor: "#F8FAFC", borderColor: "#CBD5E1" },
                }}
              >
                Customize Dashboard
              </Button>

              {/* Date Selector Pill */}
              <Button
                onClick={handleDateFilterClick}
                startIcon={<Calendar size={18} color="#2563EB" />}
                endIcon={<ChevronDown size={16} color="#64748B" />}
                sx={{
                  backgroundColor: "#FFFFFF",
                  color: "#1E293B",
                  border: "1px solid #E7EAF0",
                  borderRadius: "10px",
                  px: 2,
                  py: 0.9,
                  fontWeight: 600,
                  fontSize: "0.875rem",
                  boxShadow: "0px 1px 3px rgba(16, 24, 40, 0.04)",
                  "&:hover": { backgroundColor: "#F8FAFC", borderColor: "#CBD5E1" },
                }}
              >
                {selectedDateFilter}
              </Button>
            </>
          )}

          <Menu
            anchorEl={dateFilterAnchor}
            open={isDateFilterOpen}
            onClose={() => handleDateFilterClose()}
            PaperProps={{ sx: { borderRadius: "12px", boxShadow: "0px 4px 20px rgba(0,0,0,0.08)", minWidth: 180 } }}
          >
            {["Today", "Last 7 Days", "Last 30 Days", "May 1 – Aug 31, 2026", "This Year"].map((opt) => (
              <MenuItem key={opt} onClick={() => handleDateFilterClose(opt)}>
                <Typography variant="body2" fontWeight="500">
                  {opt}
                </Typography>
              </MenuItem>
            ))}
          </Menu>
        </Stack>
      </Stack>

      {/* Customize Mode Banner Indicator */}
      {isCustomizing && (
        <Box
          sx={{
            mb: 3,
            p: 1.5,
            px: 2.5,
            backgroundColor: "#EEF4FF",
            border: "1px solid #C7D9FE",
            borderRadius: "12px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="body2" color="#1D4ED8" fontWeight="600">
            🛠️ <strong>Customize Dashboard Mode Enabled:</strong> Drag top blue banner (::) to move widgets. Drag bottom-right corner handle (::) to resize width & height!
          </Typography>
          <Button size="small" onClick={() => setIsCatalogOpen(true)} sx={{ color: "#2563EB", fontWeight: 700 }}>
            + Manage Widgets
          </Button>
        </Box>
      )}

      {/* DND Kit Context & Dynamic Layout Grid */}
      <Box sx={{ position: "relative" }}>
        {/* 12-Column Visual Grid Guidelines Background Overlay */}
        {isCustomizing && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              pointerEvents: "none",
              zIndex: 0,
            }}
          >
            <Grid container spacing={2.5} sx={{ height: "100%" }}>
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((col) => (
                <Grid item xs={1} key={col} sx={{ height: "100%" }}>
                  <Box
                    sx={{
                      height: "100%",
                      minHeight: 400,
                      backgroundColor: "rgba(37, 99, 235, 0.03)",
                      borderLeft: "1px dashed rgba(37, 99, 235, 0.12)",
                      borderRight: "1px dashed rgba(37, 99, 235, 0.12)",
                      borderRadius: "6px",
                    }}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={sortableItemIds} strategy={rectSortingStrategy}>
          <Grid container spacing={2.5} alignItems="stretch">
            {visibleLayoutItems.map((item) => (
              <SortableWidgetWrapper
                key={item.id}
                widgetConfig={item}
                isCustomizing={isCustomizing}
                onColSpanChange={updateWidgetColSpan}
                onHeightChange={updateWidgetHeight}
                onHide={toggleWidgetHidden}
              >
                {renderWidgetContent(item.id, item.rowHeight)}
              </SortableWidgetWrapper>
            ))}
          </Grid>
        </SortableContext>
      </DndContext>
      </Box>

      {/* Widget Catalog Modal */}
      <WidgetCatalogModal
        open={isCatalogOpen}
        onClose={() => setIsCatalogOpen(false)}
        layout={layout}
        onToggleVisibility={toggleWidgetHidden}
        onResetDefault={() => {
          resetToDefault();
          setIsCatalogOpen(false);
        }}
      />
    </Box>
  );
};

export default Dashboard;
